/**
 * Beacon-cookie helper for the CSA (`_googCsa("ads")`) ad-type handler.
 *
 * Both ad types stamp the same three unit-level beacon URLs with partner
 * cookies, but the RSoC path runs its own inline loop inside
 * `google-rsoc.onResponse` (which this module delegates to wholesale via
 * rsoc.ts). This helper extracts that loop for the CSA path only; the shared
 * key set is exported so both can be reasoned about together. If the RSoC
 * inline loop is ever refactored, point it here too.
 */

import { appendPartnerCookies } from "@ascent/papi-core/client";

/**
 * Unit-level URL fields that carry partner cookies. Order is irrelevant; the
 * same set is stamped for both ad types.
 */
export const BEACON_URL_KEYS = [
  "clickBeaconUrl",
  "impressionBeaconUrl",
  "clicktrackUrl",
] as const;

/**
 * Return a shallow copy of `unit` with each known beacon URL re-stamped with
 * partner cookies. Invalid/non-string URLs are left untouched. Never mutates
 * the input.
 */
export function stampBeaconCookies<T extends Record<string, unknown>>(
  unit: T,
): T {
  const stamped: Record<string, unknown> = { ...unit };

  for (const key of BEACON_URL_KEYS) {
    const value = stamped[key];
    if (typeof value === "string") {
      try {
        const url = new URL(value);
        appendPartnerCookies(url);
        stamped[key] = url.toString();
      } catch {
        // keep original URL if parsing fails
      }
    }
  }

  return stamped as T;
}
